import { AlertTriangle, Star, Check } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../components/ui/popover";
import { AvatarBubble } from "../../../components/ui/voter-avatars";
import { isEmailShaped } from "../../../lib/identity";

/**
 * Slot-card indicators (warning / star / checkbox) shown inline next to
 * the proposed-slot date+time text. Each icon is hidden when its bucket
 * is empty (per the spec). Clicking an icon opens a popover that lists
 * the matching active overlay owners.
 *
 * In the star/checkbox popovers, an owner who ALSO has a calendar
 * conflict in the slot range gets an inline warning icon next to their
 * name with a tooltip listing the conflicting event titles.
 *
 * Props:
 *   conflict, preferred, feasible : Row[] from classifySlotsAgainstOverlays
 *   identifierByOwnerId           : map<ownerId, publicIdentifier> for tooltips
 *
 * Each Row carries: { ownerId, displayName, avatarUrl, email, isSelf,
 *                     color, hasConflict, conflictTitles }
 */
export function SlotOverlayIndicators({
  conflict = [],
  preferred = [],
  feasible = [],
  identifierByOwnerId = {},
}) {
  const hasAny = conflict.length || preferred.length || feasible.length;
  if (!hasAny) return null;

  return (
    <span
      className="ml-1.5 inline-flex shrink-0 items-center gap-1 align-middle"
      data-testid="slot-overlay-indicators"
    >
      {conflict.length > 0 ? (
        <SlotIndicatorPopover
          variant="conflict"
          icon={
            <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" />
          }
          label="Conflict"
          rows={conflict}
          identifierByOwnerId={identifierByOwnerId}
        />
      ) : null}
      {preferred.length > 0 ? (
        <SlotIndicatorPopover
          variant="preferred"
          icon={
            <Star className="h-3.5 w-3.5 text-green-600" aria-hidden="true" />
          }
          label="Preferred"
          rows={preferred}
          identifierByOwnerId={identifierByOwnerId}
        />
      ) : null}
      {feasible.length > 0 ? (
        <SlotIndicatorPopover
          variant="feasible"
          icon={
            <Check className="h-3.5 w-3.5 text-blue-600" aria-hidden="true" />
          }
          label="Feasible"
          rows={feasible}
          identifierByOwnerId={identifierByOwnerId}
        />
      ) : null}
    </span>
  );
}

function SlotIndicatorPopover({
  variant,
  icon,
  label,
  rows,
  identifierByOwnerId,
}) {
  const ariaLabel = `${rows.length} ${label.toLowerCase()}${rows.length === 1 ? "" : "s"}`;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center justify-center rounded-full p-0.5 transition-colors hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 dark:hover:bg-slate-800"
          aria-label={ariaLabel}
          data-testid={`slot-indicator-${variant}`}
        >
          {icon}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-64 p-2"
        data-testid={`slot-indicator-popover-${variant}`}
      >
        <p className="px-2 pb-1 pt-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </p>
        <ul className="flex flex-col gap-0.5">
          {rows.map((row) => {
            // Privacy: the classifier rows carry email (Model Y) but NOT the
            // owner's identifier type, so resolve the visible label from the
            // pre-computed public identifier — never `row.email`. row.displayName
            // also comes from an untyped projection, so reject it when it's
            // email-shaped and fall back to the resolved identifier.
            const identifier = identifierByOwnerId?.[row.ownerId] || null;
            const safeName =
              row.displayName && !isEmailShaped(row.displayName)
                ? row.displayName
                : identifier || "Friend";
            const name = row.isSelf ? "You" : safeName;
            return (
            <li
              key={row.ownerId}
              className="flex items-center gap-2 rounded-md px-2 py-1.5"
              data-testid={`slot-indicator-row-${variant}-${row.ownerId}`}
            >
              <span
                className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
                style={
                  row.color && !row.isSelf
                    ? {
                        boxShadow: `0 0 0 2px ${row.color}, 0 0 6px 0 ${row.color}55`,
                      }
                    : undefined
                }
                data-overlay-color={
                  row.color && !row.isSelf ? row.color : undefined
                }
              >
                <AvatarBubble
                  user={{
                    displayName: row.displayName,
                    email: row.email,
                    photoURL: row.avatarUrl,
                    publicIdentifier: identifier,
                  }}
                  label={name}
                  size={20}
                />
              </span>
              <span
                className="min-w-0 flex-1 truncate text-xs font-medium text-slate-700 dark:text-slate-200"
                title={!row.isSelf && identifier ? identifier : undefined}
              >
                {name}
              </span>
              {variant !== "conflict" && row.hasConflict ? (
                <AlertTriangle
                  className="h-3.5 w-3.5 shrink-0 text-amber-500"
                  aria-label="Has a calendar conflict in this slot range"
                  data-testid={`slot-indicator-row-warning-${row.ownerId}`}
                  title={
                    row.conflictTitles?.length
                      ? `Conflict: ${row.conflictTitles.join(", ")}`
                      : "Has a calendar conflict in this slot range"
                  }
                />
              ) : null}
            </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
